/**
 * FINDINGS-DOMAIN-1 Session 4, Surface 1 — "What's assessed at each tier".
 *
 * Read-only. Grouped by the 11 A–K risk areas rather than listed flat, because
 * the question this page answers is not "what are the 74 procedures" but "what
 * gets thinner if I buy the smaller tier" — and only the grouping shows that.
 *
 * Every number here arrives from GET /api/qoe/catalogue. There is no count, fee
 * or coefficient literal in this file by design: a screen that re-typed them
 * would drift from the report the client eventually receives.
 *
 * Fees obey PRICING_ENABLED; the scope answer stands on its own without them.
 */
import { useEffect, useMemo, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { PRICING_ENABLED } from '@/lib/pricing'
import {
  fetchCatalogue,
  fmtBand,
  fmtFee,
  type CatalogueResponse,
  type TierInfo,
} from '@/api/scope'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

function TierCard({ tier, total, selected, onSelect }: {
  tier: TierInfo
  total: number
  selected: boolean
  onSelect: () => void
}) {
  const pct = total > 0 ? Math.round((tier.finding_count / total) * 100) : 0
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`text-left rounded-lg border p-4 transition-colors w-full ${
        selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/50'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold">{tier.label}</span>
        <span className="text-xs text-muted-foreground">{fmtBand(tier)}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">
        {tier.finding_count}
        <span className="text-sm font-normal text-muted-foreground"> of {total} assessed</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded bg-muted overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-3 text-xs text-muted-foreground space-y-0.5">
        {/* R8 (QOE-TRUTH-AND-JOURNEY-1) — turnaround is not a comparison axis;
            it belongs on the quote for this engagement, once, after the tier
            is settled (see the summary line below the catalogue). Showing it
            per-card turns scope depth into a delivery-speed menu. */}
        {PRICING_ENABLED && tier.fee_formula && <div>Fee: {tier.fee_formula}</div>}
      </div>
    </button>
  )
}

export function ScopeCatalogue() {
  const { data, loading, error, execute } = useApi<CatalogueResponse>()
  const [selected, setSelected] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [showOutOfScope, setShowOutOfScope] = useState(true)

  useEffect(() => {
    void execute(fetchCatalogue)
  }, [execute])

  // Default to the deepest tier so the first paint shows the whole catalogue.
  // Picking a smaller one first would make findings appear to be missing.
  useEffect(() => {
    if (data && selected === null && data.tiers.length > 0) {
      setSelected(data.tiers[data.tiers.length - 1].key)
    }
  }, [data, selected])

  const tier = useMemo(
    () => data?.tiers.find(t => t.key === selected) ?? null,
    [data, selected],
  )

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading the assessment catalogue…</div>
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-destructive">{error}</p>
        <Button variant="outline" className="mt-3" onClick={() => void execute(fetchCatalogue)}>
          Try again
        </Button>
      </div>
    )
  }
  if (!data) return null

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold">What we assess</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data.catalogue_count} diligence procedures across {data.section_count} risk areas.
          Select a service level to see exactly which of them are performed.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.tiers.map(t => (
          <TierCard
            key={t.key}
            tier={t}
            total={data.catalogue_count}
            selected={t.key === selected}
            onSelect={() => setSelected(t.key)}
          />
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-medium">
          {tier ? `${tier.label} — coverage by risk area` : 'Coverage by risk area'}
        </h2>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showOutOfScope}
            onChange={e => setShowOutOfScope(e.target.checked)}
          />
          Show procedures not included
        </label>
      </div>

      <div className="space-y-3">
        {data.sections.map(section => {
          const inScope = selected ? (section.tier_counts[selected] ?? 0) : section.finding_count
          const open = expanded[section.letter] ?? false
          const rows = section.findings.filter(
            f => showOutOfScope || !selected || f.tiers.includes(selected),
          )
          return (
            <Card key={section.letter}>
              <CardHeader
                className="cursor-pointer"
                onClick={() => setExpanded(p => ({ ...p, [section.letter]: !open }))}
              >
                <CardTitle className="flex items-center justify-between gap-3 text-base font-medium">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-muted-foreground shrink-0">{open ? '▾' : '▸'}</span>
                    <span className="truncate">{section.letter}. {section.label}</span>
                  </span>
                  <span className="text-sm font-normal tabular-nums shrink-0 text-muted-foreground">
                    {inScope} of {section.finding_count}
                  </span>
                </CardTitle>
              </CardHeader>
              {open && (
                <CardContent className="pt-0">
                  <ul className="divide-y">
                    {rows.map(f => {
                      const included = !selected || f.tiers.includes(selected)
                      return (
                        <li key={f.number} className="py-2 flex gap-3 items-start">
                          <span
                            aria-hidden
                            className={included ? 'text-primary' : 'text-muted-foreground'}
                          >
                            {included ? '✓' : '—'}
                          </span>
                          <div className="min-w-0">
                            <div
                              className={`text-sm ${included ? '' : 'text-muted-foreground line-through'}`}
                            >
                              #{f.number} {f.title}
                              <span className="sr-only">
                                {included ? ' — included' : ' — not included'}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {f.procedure}
                            </div>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </CardContent>
              )}
            </Card>
          )
        })}
      </div>

      {tier && (
        <p className="text-sm text-muted-foreground">
          {tier.label}: {tier.finding_count} of {data.catalogue_count} procedures
          {PRICING_ENABLED && tier.fee_base !== null && (
            <> · from {fmtFee(tier.fee_base)}{tier.fee_formula ? ` (${tier.fee_formula})` : ''}</>
          )}
          {tier.turnaround ? ` · ${tier.turnaround}` : ''}
        </p>
      )}
    </div>
  )
}

export default ScopeCatalogue
